import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import formatDate from './../utils/formatDate';
import isEqual from 'lodash.isequal';
import { useLocalStorage } from './../hooks/useLocalStorage';

export const defaultFilters = {
  visorRef: '',
  analystUsername: '',
  positive: 0,
  quarantine: 0,
  status: 2,
  action: 0,
  showFavouritesOnly: false,
  dateRange: [
    {
      startDate: new Date(new Date().setHours(0, 0, 0, 0)),
      endDate: new Date(new Date().setHours(23, 59, 59, 999)),
      key: 'selection',
    },
  ],
  startTime: '',
  endTime: '',
  category: [], 
  isBlankCategory: false,
  isPreviouslyReported: false,
};

export const defaultPagination = {
  currentPage: 1,
  pageSize: 200,
};

const FilterContext = createContext();
const BASENAME = process.env.REACT_APP_BASENAME || '';

// Whitelist of allowed paths to prevent open redirect
const ALLOWED_APP_PATHS = [
  '/',                // landing route
  '/reports',
  '/reports/daily',
  '/reports/audit',
  '/reports/licence',
  '/reports/monitoring-service',
  '/devices',
  '/users',
  '/heartbeat',
];

// Strip / add basename helpers
const stripBasename = (path) =>
    BASENAME && path.startsWith(BASENAME)
    ? path.slice(BASENAME.length) || '/'
    : path;

const addBasename = (appPath) =>
  BASENAME
    ? (appPath.startsWith('/') ? `${BASENAME}${appPath}` : `${BASENAME}/${appPath}`)
    : appPath;

// Helper function to get a safe navigation path
const getSafeAppPath = (appPathname) => {
  if (ALLOWED_APP_PATHS.includes(appPathname)) return appPathname;

  const isAllowedSubPath = ALLOWED_APP_PATHS.some((allowed) =>
    appPathname.startsWith(allowed + '/') &&
    !appPathname.includes('..') &&
    !appPathname.includes('//') &&
    appPathname.split('/').length <= 10
  );
  return isAllowedSubPath ? appPathname : '/'; // default to app root
};

function getStoredFilters() {
  const stored = sessionStorage.getItem('filters');
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);

    if (parsed.dateRange && Array.isArray(parsed.dateRange)) {
      parsed.dateRange = parsed.dateRange.map((range) => ({
        ...range,
        startDate: new Date(range.startDate),
        endDate: new Date(range.endDate),
      }));
    }

    if (parsed.startTime) parsed.startTime = new Date(parsed.startTime);
    if (parsed.endTime) parsed.endTime = new Date(parsed.endTime);

    return parsed;
  } catch {
    return null;
  }
}

function getStoredPagination() {
  const stored = sessionStorage.getItem('pagination');
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

function parseParamsToFilters(searchParams) {
  const params = Object.fromEntries([...searchParams.entries()]);
  const parsed = { ...defaultFilters };

  ['positive', 'quarantine', 'status', 'action'].forEach((key) => {
    if (params[key]) parsed[key] = Number(params[key]);
  });

  if (params.showFavouritesOnly === 'true') {
    parsed.showFavouritesOnly = true;
  }

  if (params.startDate && params.endDate) {
    parsed.dateRange = [
      {
        startDate: new Date(params.startDate),
        endDate: new Date(params.endDate),
        key: 'selection',
      },
    ];
  }

  if (params.startTime) parsed.startTime = new Date(params.startTime);
  if (params.endTime) parsed.endTime = new Date(params.endTime);

  return parsed;
}

function parseParamsToPagination(searchParams) {
  const params = Object.fromEntries([...searchParams.entries()]);
  const page = Number(params.page);
  const pageSize = Number(params.pageSize);

  return {
    currentPage: !isNaN(page) && page > 0 ? page : defaultPagination.currentPage,
    pageSize: !isNaN(pageSize) && pageSize > 0 ? pageSize : defaultPagination.pageSize,
  };
}

function updateURLParams(filters, pagination, setSearchParams) {
  const params = {};

  if (filters.positive !== defaultFilters.positive) {
    params.positive = filters.positive;
  }
  if (filters.quarantine !== defaultFilters.quarantine) {
    params.quarantine = filters.quarantine;
  }
  if (filters.status !== defaultFilters.status) {
    params.status = filters.status;
  }
  if (filters.action !== defaultFilters.action) {
    params.action = filters.action;
  }
  if (filters.showFavouritesOnly !== defaultFilters.showFavouritesOnly) {
    params.showFavouritesOnly = filters.showFavouritesOnly;
  }
if (filters.SortColumn !== defaultFilters.SortColumn) {
  params.sortColumn = filters.SortColumn;
}

if (filters.SortAsc !== defaultFilters.SortAsc) {
  params.sortAsc = filters.SortAsc;
}
const { startDate, endDate } = filters.dateRange[0];
const defaultStart = defaultFilters.dateRange[0].startDate;
const defaultEnd = defaultFilters.dateRange[0].endDate;

if (
  startDate && endDate &&
  (startDate.getTime() !== defaultStart.getTime() || endDate.getTime() !== defaultEnd.getTime())
) {
  params.startDate = formatDate(startDate);
  params.endDate = formatDate(endDate);
}

  if (filters.startTime) {
    params.startTime = formatDate(filters.startTime);
  }
  if (filters.endTime) {
    params.endTime = formatDate(filters.endTime);
  }

  if (pagination.currentPage !== defaultPagination.currentPage) {
    params.page = pagination.currentPage;
  }
  if (pagination.pageSize !== defaultPagination.pageSize) {
    params.pageSize = pagination.pageSize;
  }

  setSearchParams(params, { replace: true });
}

export const FilterProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const storedFilters = getStoredFilters() || defaultFilters;
  const storedPagination = getStoredPagination() || defaultPagination;

  const urlFilters = parseParamsToFilters(searchParams);
  const urlPagination = parseParamsToPagination(searchParams);
  const [isCollapsed, setIsCollapsed] = useLocalStorage('filtersCollapsed', false);
  
  function mergeFilters(stored, fromUrl) {
    if (!isEqual(fromUrl, defaultFilters)) {
      return fromUrl;
    }
    return stored;
  }

  function mergePagination(stored, fromUrl) {
    if (!isEqual(fromUrl, defaultPagination)) {
      return fromUrl;
    }
    return stored;
  }

  const [filters, setFilters] = useState(mergeFilters(storedFilters, urlFilters));
  const [pagination, setPagination] = useState(mergePagination(storedPagination, urlPagination));

  useEffect(() => {
    const { visorRef, ...rest } = filters;
    sessionStorage.setItem('filters', JSON.stringify(rest));
  }, [filters]);

  useEffect(() => {
    sessionStorage.setItem('pagination', JSON.stringify(pagination));
  }, [pagination]);

  useEffect(() => {
    updateURLParams(filters, pagination, setSearchParams);
  }, [filters, pagination, setSearchParams]);

  // Reset without navigation - for use in components
  const resetFiltersOnly = () => {
    setFilters(defaultFilters);
    setPagination(defaultPagination);

    sessionStorage.removeItem('filters');
    sessionStorage.removeItem('pagination');
    
    // Clear URL params without navigating
    setSearchParams({}, { replace: true });
  };

  // Reset with navigation - for use when you want to redirect
  const resetFilters = () => {
    setFilters(defaultFilters);
    setPagination(defaultPagination);

    sessionStorage.removeItem('filters');
    sessionStorage.removeItem('pagination');

    const appPath = stripBasename(location.pathname);
    const safeAppPath = getSafeAppPath(appPath);

    navigate(safeAppPath, { replace: true });
  };

  return (
    <FilterContext.Provider value={{ 
      filters, 
      setFilters, 
      pagination, 
      setPagination, 
      resetFilters,
      resetFiltersOnly,
      isCollapsed, 
      setIsCollapsed 
    }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = () => useContext(FilterContext);
